import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { AnalysisResult } from '../components/dashboard/AnalysisResult';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { getAnalysis } from '../services/api';
import type { Analysis as AnalysisType } from '../types';

export default function Analysis() {
  const { id } = useParams<{ id: string }>();
  const [analysis, setAnalysis] = useState<AnalysisType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchAnalysis();
    }
  }, [id]);

  const fetchAnalysis = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const data = await getAnalysis(id);
      setAnalysis(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (error || !analysis) {
    return (
      <AppLayout>
        <Card className="p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Erro</h2>
          <p className="text-gray-400 mb-6">{error || 'Análise não encontrada'}</p>
          <Link to="/dashboard">
            <Button>Voltar ao Dashboard</Button>
          </Link>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/dashboard" className="text-blue-400 hover:text-blue-300">
            ← Voltar ao Dashboard
          </Link>
        </div>
        
        <Card className="p-6 mb-6">
          <h1 className="text-2xl font-bold mb-2">Análise do Vídeo</h1>
          <p className="text-gray-400 text-sm">
            URL: <a href={analysis.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
              {analysis.video_url}
            </a>
          </p>
          <p className="text-gray-400 text-sm mt-2">
            Status: <span className={`font-semibold ${
              analysis.status === 'completed' ? 'text-green-400' :
              analysis.status === 'failed' ? 'text-red-400' :
              'text-yellow-400'
            }`}>
              {analysis.status}
            </span>
          </p>
        </Card>

        {analysis.status === 'completed' && analysis.analysis_result && (
          <AnalysisResult analysis={analysis} />
        )}

        {analysis.status === 'processing' && (
          <Card className="p-8 text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-400">Processando análise...</p>
          </Card>
        )}

        {analysis.status === 'failed' && (() => {
          const errorMessage = analysis.analysis_result || '';
          const isTranscriptDisabled = /transcri[çc][õo]es?\s*desabilitada/i.test(errorMessage) || 
                                       /transcript.*disabled/i.test(errorMessage);
          
          return (
            <Card className="p-8">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">⚠️</div>
                <h2 className="text-2xl font-bold text-red-400 mb-2">
                  {isTranscriptDisabled 
                    ? 'Transcrições Desabilitadas' 
                    : 'Erro ao Processar Vídeo'}
                </h2>
                <p className="text-gray-400 mb-4">
                  {isTranscriptDisabled 
                    ? 'Este vídeo não possui transcrições disponíveis. Alguns criadores desabilitam as transcrições automáticas ou manuais para seus vídeos.'
                    : 'Não foi possível processar o vídeo solicitado. Por favor, tente novamente mais tarde.'}
                </p>
              </div>
              
              {isTranscriptDisabled && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-amber-400 mb-2">Informação:</h3>
                  <p className="text-gray-300">
                    Para analisar um vídeo, é necessário que ele possua transcrições habilitadas. 
                    Infelizmente, este vídeo específico não possui transcrições disponíveis.
                  </p>
                </div>
              )}

              {!isTranscriptDisabled && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-red-400 mb-2">Detalhes Técnicos:</h3>
                  <p className="text-gray-300 text-sm font-mono">
                    {errorMessage.replace(/^Error:\s*/i, '')}
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/dashboard">
                  <Button variant="outline">Voltar ao Dashboard</Button>
                </Link>
                <Link to="/dashboard" onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/dashboard';
                }}>
                  <Button>Nova Análise</Button>
                </Link>
              </div>

              {analysis.video_url && (
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <p className="text-sm text-gray-400 mb-2">Vídeo analisado:</p>
                  <a 
                    href={analysis.video_url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-400 hover:text-blue-300 text-sm break-all"
                  >
                    {analysis.video_url}
                  </a>
                </div>
              )}
            </Card>
          );
        })()}
      </div>
    </AppLayout>
  );
}

